import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useSelector } from "react-redux";

const faqData = [
  { 
    question: { fr: "Comment suivre ma commande ?", ar: "كيف أتابع طلبي؟" },
    answer: {
      fr: "Rendez-vous sur la page 'Suivi de commande' et entrez votre numéro de commande.",
      ar: "اذهب إلى صفحة 'تتبع الطلب' وأدخل رقم الطلب الخاص بك."
    }
  },
  { 
    question: { fr: "Quels sont les frais et les délais de livraison ?", ar: "ما هي أسعار و مواعيد التوصيل ؟" },
    answer: {
      fr: (
        <div>
          <p>Nos délais de livraison varient entre 2 et 5 jours ouvrés.
            Les prix :
          </p>
          <div className="flex justify-center mt-4">
            <a 
              href="/frais_livraison.pdf" 
              download="Frais_de_Livraison.pdf"
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-800 transition duration-200"
            >
              📄 Télécharger le PDF
            </a>
          </div>
        </div>
      ),
      ar: (
        <div>
          <p dir="rtl">
            
          تتراوح مواعيد التسليم لدينا بين 2 و 5 أيام عمل.
                         الأسعار</p>
          <div className="flex justify-center mt-4">
            <a 
              href="/frais_livraison.pdf" 
              download="Frais_de_Livraison.pdf"
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-800 transition duration-200"
            >
              📄 تنزيل الـ PDF
            </a>
          </div>
        </div>
      )
    }
  },
  { 
    question: { fr: "Comment retourner un produit ?", ar: "كيف أرجع منتجاً؟" },
    answer: {
      fr: "Vous pouvez retourner un produit sous 14 jours en remplissant le formulaire de retour.",
      ar: "يمكنك إرجاع المنتج في غضون 14 يومًا عن طريق ملء نموذج الإرجاع."
    }
  }
];

function FAQSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [openIndex, setOpenIndex] = useState(null);
  const maxChars = 400; // Limite de caractères

  const langue = useSelector((state) => state.langue.value); // Langue actuelle

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const message = `
📩 *Nouveau Message du Formulaire (Client)* 📩

👤 *Nom:* ${formData.name}
✉️ *Email:* ${formData.email}
📌 *Sujet:* ${formData.subject}
📝 *Message:* ${formData.message}
  `;

    try {
      const response = await fetch(
        `https://api.telegram.org/bot${process.env.VITE_TELEGRAM_BOT_TOKEN}/sendMessage`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            chat_id: process.env.VITE_TELEGRAM_CHAT_ID,
            text: message,
            parse_mode: "Markdown",
          }),
        }
      );

      if (response.ok) {
        alert("✅ Message envoyé avec succès !");
        setFormData({ name: "", email: "", subject: "", message: "" }); // Reset form
      } else {
        alert("❌ Erreur lors de l'envoi du message !");
      }
    } catch (error) {
      alert("⚠️ Impossible d'envoyer le message.");
    }
  };

  return (
    <div className="lg:px-36 w-full mx-auto p-6 bg-gray-300">
      <h2 className="text-3xl text-green-800 font-bold text-center mb-4">
        {langue === "fr" ? "Foire Aux Questions (FAQ)" : "الأسئلة الشائعة (FAQ)"}
      </h2>
      <div className="space-y-4">
        {faqData.map((item, index) => (
          <div key={index} className="border border-gray-500 rounded-lg">
            <button
              className="w-full p-3 flex justify-between items-center"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              {langue === "fr" ? item.question.fr : item.question.ar}
              <ChevronDown className={`transition-transform ${openIndex === index ? "rotate-180" : ""}`} />
            </button>
            {openIndex === index && (
              <div className="p-3 border-t">
                {langue === "fr" ? item.answer.fr : item.answer.ar}
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className="p-4">
        <h2 className="text-2xl font-bold mb-2 text-green-800 text-center">
          {langue === "fr" ? "Besoin d'aide ?" : "هل تحتاج إلى مساعدة؟"}
        </h2>
        <p className="m-2 text-black font-medium">
          {langue === "fr" ? (
            <>
              En cas de problème, n'hésitez pas à nous envoyer un message en incluant votre <strong>nom</strong>, <strong>prénom</strong> et une <strong>description détaillée</strong> de votre problème ou question.
            </>
          ) : (
            <>
              في حالة وجود مشكلة، لا تتردد في إرسال رسالة لنا تتضمن <strong>اسمك</strong>، <strong>لقبك</strong> و <strong>وصف مفصل</strong> لمشكلتك أو سؤالك.
            </>
          )}
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            name="name"
            placeholder={langue === "fr" ? "Votre Nom" : "اسمك"}
            value={formData.name}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500"
            required
          />
          <input
            type="email"
            name="email"
            placeholder={langue === "fr" ? "Votre Email" : "بريدك الإلكتروني"}
            value={formData.email}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500"
            required
          />
          <input
            type="text"
            name="subject"
            placeholder={langue === "fr" ? "Sujet" : "الموضوع"}
            value={formData.subject}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500"
            required
          />
          <textarea
            name="message"
            placeholder={langue === "fr" ? "Votre message..." : "رسالتك..."}
            value={formData.message}
            onChange={(e) => {
              if (e.target.value.length <= maxChars) {
                handleChange(e);
              }
            }}
            className="w-full p-2 border rounded-md h-32 focus:ring-2 focus:ring-green-500"
            required
          ></textarea>
          <div className="flex flex-row items-center justify-between">
            <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition duration-200">
              {langue === "fr" ? "Envoyer" : "إرسال"}
            </button>
            <p className="text-gray-500">{formData.message.length} / {maxChars} caractères</p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FAQSection;
